import * as THREE from 'three';

import { CandidateInfo, MappingResult } from '../recognize/config';
import { ChainMapping } from '../schema/mapping';
import {
  type ChainSolverProperties,
  SolverProperties,
  SpringSolverProperties,
  isSpringSolverProperties,
} from '../schema/solver';
import { BoneProperties, type Skeleton } from '../skeleton/core';
import type { EffectorId, IKSession } from '../solvers/ikSession';

export const CARTOGRAPHER_HISTORY_LIMIT = 45;

export type CartographerHistoryKind =
  | 'baseline'
  | 'mapping'
  | 'pose'
  | 'bone-properties'
  | 'ik';

export interface CartographerHistoryEffectorSnapshot {
  id: EffectorId;
  goalPosition: [number, number, number];
  poleTarget: [number, number, number];
  active: boolean;
}

export interface CartographerHistoryIkSnapshot {
  enabled: boolean;
  effectors: CartographerHistoryEffectorSnapshot[];
}

export interface CartographerHistorySnapshot {
  mapping: MappingResult | null;
  bonePropertiesByBoneRef: Record<string, BoneProperties>;
  excludedBoneNames: string[];
  poseLocals: THREE.Matrix4[] | null;
  ik: CartographerHistoryIkSnapshot;
  mappingVersion: number;
  bonePropertiesVersion: number;
}

export interface CartographerHistoryEntry {
  id: string;
  kind: CartographerHistoryKind;
  label: string;
  snapshot: CartographerHistorySnapshot;
}

export interface CartographerHistoryItem {
  id: string;
  kind: CartographerHistoryKind;
  label: string;
  mappingVersion: number;
  bonePropertiesVersion: number;
  saved: boolean;
}

interface CaptureCartographerHistorySnapshotInput {
  mapping: MappingResult | null;
  skeleton: Skeleton | null;
  excludedBoneNames: string[];
  ikEnabled: boolean;
  ikSession: IKSession | null;
  mappingVersion: number;
  bonePropertiesVersion: number;
}

function cloneStructuredData<T>(value: T): T {
  if (value === undefined) {
    return value;
  }
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

function cloneSolverProperties(
  solverProperties: ChainSolverProperties | null | undefined,
): ChainSolverProperties | null {
  if (!solverProperties) return null;
  if (isSpringSolverProperties(solverProperties)) {
    return new SpringSolverProperties(
      solverProperties.stiffness,
      solverProperties.falloff,
      solverProperties.falloffCurve,
      solverProperties.gain,
    );
  }
  return new SolverProperties(
    solverProperties.iterations,
    solverProperties.reversePv,
    solverProperties.maxStretch,
  );
}

function cloneChainMapping(mapping: ChainMapping): ChainMapping {
  return new ChainMapping({
    name: mapping.name,
    startBone: mapping.startBone,
    endBone: mapping.endBone,
    boneNames: [...mapping.boneNames],
    ikGoal: mapping.ikGoal,
    confidence: mapping.confidence,
    solverType: mapping.solverType,
    solverParams: cloneStructuredData(mapping.solverParams),
    solverProperties: cloneSolverProperties(mapping.solverProperties),
    legType: mapping.legType,
  });
}

function cloneCandidateInfo(candidate: CandidateInfo): CandidateInfo {
  return new CandidateInfo({
    boneName: candidate.boneName,
    score: candidate.score,
    checkResults: candidate.checkResults.map(([state, reason]) => [state, reason]),
    categoryResults: Object.fromEntries(
      Object.entries(candidate.categoryResults).map(([category, checks]) => [
        category,
        checks.map(([state, reason, score, weight]) => [
          state,
          reason,
          score,
          weight,
        ]),
      ]),
    ),
    categoryScores: { ...candidate.categoryScores },
    evidence: cloneStructuredData(candidate.evidence),
  }) as CandidateInfo;
}

export function cloneMappingResult(mapping: MappingResult | null): MappingResult | null {
  if (!mapping) return null;

  return new MappingResult({
    roleToBone: { ...mapping.roleToBone },
    roleToConfidence: { ...mapping.roleToConfidence },
    chains: Object.fromEntries(
      Object.entries(mapping.chains).map(([chainName, boneNames]) => [
        chainName,
        [...boneNames],
      ]),
    ),
    chainResults: Object.fromEntries(
      Object.entries(mapping.chainResults).map(([chainName, chainMapping]) => [
        chainName,
        cloneChainMapping(chainMapping),
      ]),
    ),
    warnings: [...mapping.warnings],
    explain: Object.fromEntries(
      Object.entries(mapping.explain).map(([role, breakdown]) => [
        role,
        { ...breakdown },
      ]),
    ),
    presetName: mapping.presetName,
    presetId: mapping.presetId,
    detectedFeatures: cloneStructuredData(mapping.detectedFeatures),
    excludedBones: [...mapping.excludedBones],
    candidates: Object.fromEntries(
      Object.entries(mapping.candidates).map(([role, candidates]) => [
        role,
        candidates.map(cloneCandidateInfo),
      ]),
    ),
    roleToDetectionMethod: { ...mapping.roleToDetectionMethod },
    sideConvention: mapping.sideConvention,
    namingMode: mapping.namingMode,
    forwardAxis: mapping.forwardAxis,
    upAxis: mapping.upAxis,
    ioExtras: cloneStructuredData(mapping.ioExtras),
  });
}

export function cloneBoneProperties(
  boneProperties: BoneProperties | null | undefined,
): BoneProperties | null {
  if (!boneProperties) return null;
  return BoneProperties.fromDict(boneProperties.toDict());
}

export function captureBonePropertiesSnapshot(
  skeleton: Skeleton | null,
): Record<string, BoneProperties> {
  if (!skeleton) return {};

  const snapshot: Record<string, BoneProperties> = {};
  for (const bone of skeleton.bones) {
    const cloned = cloneBoneProperties(bone.properties);
    if (!cloned) continue;
    snapshot[bone.internalRef] = cloned;
  }
  return snapshot;
}

export function restoreBonePropertiesSnapshot(
  skeleton: Skeleton | null,
  bonePropertiesByBoneRef: Record<string, BoneProperties>,
) {
  if (!skeleton) return;

  for (const bone of skeleton.bones) {
    bone.properties = cloneBoneProperties(
      bonePropertiesByBoneRef[bone.internalRef] ?? null,
    );
  }
}

export function clonePoseLocals(
  poseLocals: THREE.Matrix4[] | null | undefined,
): THREE.Matrix4[] | null {
  if (!poseLocals) return null;
  return poseLocals.map((matrix) => matrix.clone());
}

export function capturePoseLocalsSnapshot(
  skeleton: Skeleton | null,
): THREE.Matrix4[] | null {
  if (!skeleton) return null;
  return skeleton.bones.map((bone) => bone.poseLocal.clone());
}

export function captureIkSnapshot(
  ikSession: IKSession | null,
  enabled: boolean,
): CartographerHistoryIkSnapshot {
  if (!ikSession) {
    return { enabled, effectors: [] };
  }

  const effectors = [...ikSession.effectors.values()]
    .map((effector) => ({
      id: effector.id,
      goalPosition: [
        effector.goalPosition.x,
        effector.goalPosition.y,
        effector.goalPosition.z,
      ] as [number, number, number],
      poleTarget: [
        effector.poleTarget.x,
        effector.poleTarget.y,
        effector.poleTarget.z,
      ] as [number, number, number],
      active: effector.active,
    }))
    .sort((left, right) => left.id.localeCompare(right.id));

  return { enabled, effectors };
}

export function cloneIkSnapshot(
  snapshot: CartographerHistoryIkSnapshot,
): CartographerHistoryIkSnapshot {
  return {
    enabled: snapshot.enabled,
    effectors: snapshot.effectors.map((effector) => ({
      id: effector.id,
      goalPosition: [...effector.goalPosition] as [number, number, number],
      poleTarget: [...effector.poleTarget] as [number, number, number],
      active: effector.active,
    })),
  };
}

export function applyIkSnapshotToSession(
  ikSession: IKSession | null,
  snapshot: CartographerHistoryIkSnapshot,
) {
  if (!ikSession) return;

  const snapshotById = new Map(
    snapshot.effectors.map((effector) => [effector.id, effector]),
  );

  for (const [effectorId, effector] of ikSession.effectors.entries()) {
    const saved = snapshotById.get(effectorId);
    if (!saved) {
      effector.active = false;
      continue;
    }
    effector.goalPosition.set(
      saved.goalPosition[0],
      saved.goalPosition[1],
      saved.goalPosition[2],
    );
    effector.poleTarget.set(
      saved.poleTarget[0],
      saved.poleTarget[1],
      saved.poleTarget[2],
    );
    effector.active = saved.active;
  }
}

export function captureCartographerHistorySnapshot({
  mapping,
  skeleton,
  excludedBoneNames,
  ikEnabled,
  ikSession,
  mappingVersion,
  bonePropertiesVersion,
}: CaptureCartographerHistorySnapshotInput): CartographerHistorySnapshot {
  const effectiveExcludedBoneNames =
    (mapping?.excludedBones.length ?? 0) > 0
      ? [...(mapping?.excludedBones ?? [])]
      : [...excludedBoneNames];

  return {
    mapping: cloneMappingResult(mapping),
    bonePropertiesByBoneRef: captureBonePropertiesSnapshot(skeleton),
    excludedBoneNames: effectiveExcludedBoneNames,
    poseLocals: capturePoseLocalsSnapshot(skeleton),
    ik: captureIkSnapshot(ikSession, ikEnabled),
    mappingVersion,
    bonePropertiesVersion,
  };
}

export function cloneCartographerHistorySnapshot(
  snapshot: CartographerHistorySnapshot,
): CartographerHistorySnapshot {
  return {
    mapping: cloneMappingResult(snapshot.mapping),
    bonePropertiesByBoneRef: Object.fromEntries(
      Object.entries(snapshot.bonePropertiesByBoneRef).map(
        ([boneRef, boneProperties]) => [
          boneRef,
          cloneBoneProperties(boneProperties)!,
        ],
      ),
    ),
    excludedBoneNames: [...snapshot.excludedBoneNames],
    poseLocals: clonePoseLocals(snapshot.poseLocals),
    ik: cloneIkSnapshot(snapshot.ik),
    mappingVersion: snapshot.mappingVersion,
    bonePropertiesVersion: snapshot.bonePropertiesVersion,
  };
}

function createHistoryId() {
  return `cartographer-history-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createCartographerHistoryEntry(input: {
  kind: CartographerHistoryKind;
  label: string;
  snapshot: CartographerHistorySnapshot;
}): CartographerHistoryEntry {
  return {
    id: createHistoryId(),
    kind: input.kind,
    label: input.label,
    snapshot: cloneCartographerHistorySnapshot(input.snapshot),
  };
}

export function appendCartographerHistoryEntry(
  history: CartographerHistoryEntry[],
  historyIndex: number,
  entry: CartographerHistoryEntry,
  maxLength = CARTOGRAPHER_HISTORY_LIMIT,
) {
  const nextHistory = history.slice(0, historyIndex + 1);
  nextHistory.push(entry);

  if (nextHistory.length > maxLength) {
    nextHistory.splice(0, nextHistory.length - maxLength);
  }

  return {
    history: nextHistory,
    historyIndex: nextHistory.length - 1,
  };
}

function buildComparableSnapshot(snapshot: CartographerHistorySnapshot) {
  return {
    mapping:
      snapshot.mapping === null
        ? null
        : {
            roleToBone: Object.fromEntries(
              Object.entries(snapshot.mapping.roleToBone).sort(([left], [right]) =>
                left.localeCompare(right),
              ),
            ),
            roleToConfidence: Object.fromEntries(
              Object.entries(snapshot.mapping.roleToConfidence).sort(
                ([left], [right]) => left.localeCompare(right),
              ),
            ),
            chains: Object.fromEntries(
              Object.entries(snapshot.mapping.chains)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([chainName, boneNames]) => [chainName, [...boneNames]]),
            ),
            chainResults: Object.fromEntries(
              Object.entries(snapshot.mapping.chainResults)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([chainName, chainMapping]) => [
                  chainName,
                  {
                    name: chainMapping.name,
                    startBone: chainMapping.startBone,
                    endBone: chainMapping.endBone,
                    boneNames: [...chainMapping.boneNames],
                    ikGoal: chainMapping.ikGoal,
                    confidence: chainMapping.confidence,
                    solverType: chainMapping.solverType,
                    solverParams: cloneStructuredData(chainMapping.solverParams),
                    solverProperties: chainMapping.solverProperties
                      ? chainMapping.solverProperties.toDict()
                      : null,
                    legType: chainMapping.legType,
                  },
                ]),
            ),
            warnings: [...snapshot.mapping.warnings],
            explain: Object.fromEntries(
              Object.entries(snapshot.mapping.explain)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([role, breakdown]) => [role, { ...breakdown }]),
            ),
            presetName: snapshot.mapping.presetName,
            presetId: snapshot.mapping.presetId,
            detectedFeatures: cloneStructuredData(snapshot.mapping.detectedFeatures),
            excludedBones: [...snapshot.mapping.excludedBones],
            candidates: Object.fromEntries(
              Object.entries(snapshot.mapping.candidates)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([role, candidates]) => [
                  role,
                  candidates.map((candidate) => ({
                    boneName: candidate.boneName,
                    score: candidate.score,
                    checkResults: candidate.checkResults.map(([state, reason]) => [
                      state,
                      reason,
                    ]),
                    categoryResults: Object.fromEntries(
                      Object.entries(candidate.categoryResults)
                        .sort(([left], [right]) => left.localeCompare(right))
                        .map(([category, checks]) => [
                          category,
                          checks.map(([state, reason, score, weight]) => [
                            state,
                            reason,
                            score,
                            weight,
                          ]),
                        ]),
                    ),
                    categoryScores: { ...candidate.categoryScores },
                    evidence: cloneStructuredData(candidate.evidence),
                  })),
                ]),
            ),
            roleToDetectionMethod: Object.fromEntries(
              Object.entries(snapshot.mapping.roleToDetectionMethod).sort(
                ([left], [right]) => left.localeCompare(right),
              ),
            ),
            sideConvention: snapshot.mapping.sideConvention,
            namingMode: snapshot.mapping.namingMode,
            forwardAxis: snapshot.mapping.forwardAxis,
            upAxis: snapshot.mapping.upAxis,
            ioExtras: cloneStructuredData(snapshot.mapping.ioExtras),
          },
    bonePropertiesByBoneRef: Object.fromEntries(
      Object.entries(snapshot.bonePropertiesByBoneRef)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([boneRef, boneProperties]) => [boneRef, boneProperties.toDict()]),
    ),
    excludedBoneNames: [...snapshot.excludedBoneNames].sort(),
    poseLocals:
      snapshot.poseLocals?.map((matrix) =>
        matrix.elements.map((value) => Number(value.toFixed(6))),
      ) ?? null,
    ik: {
      enabled: snapshot.ik.enabled,
      effectors: snapshot.ik.effectors.map((effector) => ({
        id: effector.id,
        goalPosition: [...effector.goalPosition],
        poleTarget: [...effector.poleTarget],
        active: effector.active,
      })),
    },
  };
}

export function getCartographerHistorySnapshotSignature(
  snapshot: CartographerHistorySnapshot,
) {
  return JSON.stringify(buildComparableSnapshot(snapshot));
}

export function areCartographerHistorySnapshotsEqual(
  left: CartographerHistorySnapshot,
  right: CartographerHistorySnapshot,
) {
  return (
    getCartographerHistorySnapshotSignature(left) ===
    getCartographerHistorySnapshotSignature(right)
  );
}

export function toCartographerHistoryItem(
  entry: CartographerHistoryEntry,
  savedEntryId: string | null,
): CartographerHistoryItem {
  return {
    id: entry.id,
    kind: entry.kind,
    label: entry.label,
    mappingVersion: entry.snapshot.mappingVersion,
    bonePropertiesVersion: entry.snapshot.bonePropertiesVersion,
    saved: savedEntryId === entry.id,
  };
}
